import React, {
    createContext,
    useContext,
    useReducer,
    useMemo,
    useEffect
} from "react";
import isEqual from "lodash/isEqual";

import { usePhotoUpload } from "frontend/hooks/usePhotoUpload";

/*---------------------------
| Select Context
---------------------------*/
export const SelectContext = createContext();
import reducer from "./reducer";
import getDefaultState from "./getDefaultState";
import * as Actions from "./actions";
import * as Utils from "./utils";

/*---------------------------
| InnerWrapper for onMount and global sideEffect Listeners
---------------------------*/
const InnerWrapper = ({ children, uploadIds }) => {
    const { state, dispatch } = useContext(SelectContext);
    const { uploadIds: previousUploadIds } = state;

    // Keep In Sync with Consumer: In case consumer changes which ids are present
    useEffect(() => {
        if (!isEqual(previousUploadIds, uploadIds)) {
            dispatch(Actions.updateUploadIds(uploadIds));
        }
    }, [uploadIds]);

    return <>{children}</>;
};

/*---------------------------
| Providers
---------------------------*/
// Default
export const SelectProvider = ({
    children,
    uploadIds = [],
    defaultUploadIdsSelected = []
}) => {
    const defaultState = {
        uploadIds
    };

    const initialArg = getDefaultState(defaultState, defaultUploadIdsSelected);
    const [state, dispatch] = useReducer(reducer, initialArg);

    // useMemo so it does not pass value on every render
    const value = useMemo(() => ({ state, dispatch }), [state, dispatch]);

    return (
        <SelectContext.Provider value={value} displayName={"SelectContext"}>
            <InnerWrapper uploadIds={uploadIds}>{children}</InnerWrapper>
        </SelectContext.Provider>
    );
};

// DropZone Provider
export const SelectProviderForDropzone = ({
    children,
    selectedUploadIds = []
}) => {
    const { editableUploads } = usePhotoUpload();
    return (
        <SelectProvider
            uploadIds={editableUploads.map(u => u.id)}
            defaultUploadIdsSelected={selectedUploadIds}
        >
            {children}
        </SelectProvider>
    );
};

/*---------------------------
| Hooks
---------------------------*/
// useSelect
export const useSelect = () => {
    const { state, dispatch } = useContext(SelectContext);
    const { uploadsSelected } = state;
    const uploadCount = Utils.getUploadIds(uploadsSelected).length;

    const onClick = (e, id, gridIndex) => {
        // Only Range select if there are more than 2 uploads in grid
        if (uploadCount > 2 && e.shiftKey) {
            return dispatch(
                Actions.selectDeselectRange({
                    id,
                    gridIndex
                })
            );
        }
        dispatch(Actions.toggleId(id, gridIndex));
    };
    const selectAll = () => {
        dispatch(Actions.selectAll());
    };
    const deselectAll = () => {
        dispatch(Actions.deselectAll());
    };
    const selectIds = ids => {
        dispatch(Actions.selectIds(ids));
    };
    const deselectIds = ids => {
        dispatch(Actions.deselectIds(ids));
    };

    return {
        ...state,
        onClick,
        getSelectedUploadIds: Utils.getSelectedUploadIds,
        selectAll,
        deselectAll,
        selectIds,
        deselectIds // not currently being used
    };
};
